"use client";

import { ArrowRight, Asterisk } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const team = [{
  name: "Ing. Roberto Kalisch",
  role: "DIRECTOR GENERAL",
  bio: "Líder visionario con más de 30 años transformando la industria del acero en México a través de la innovación y calidad.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1679911/img/equipo_roberto_kalisch.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1679911/img/equipo_roberto_kalisch.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1679911/img/equipo_roberto_kalisch.png-wvc-sizes"
}, {
  name: "Elena Martínez",
  role: "OPERACIONES ESTRUCTURALES",
  bio: "Experta en logística y procesos industriales, asegurando que cada viga y placa cumpla con los estándares internacionales.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1679911/img/equipo_elena_martinez.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1679911/img/equipo_elena_martinez.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1679911/img/equipo_elena_martinez.png-wvc-sizes"
}, {
  name: "Carlos Ruiz",
  role: "INGENIERÍA DE PROYECTOS",
  bio: "Especialista en diseño estructural y optimización de materiales para grandes infraestructuras y desarrollos urbanos.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1679911/img/equipo_carlos_ruiz.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1679911/img/equipo_carlos_ruiz.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1679911/img/equipo_carlos_ruiz.png-wvc-sizes"
}];
export default function NuestroEquipo() {
  const {
    ref: headerRef,
    isVisible: headerVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  return <section data-section-id="1848" id="nuestro-equipo" className="bg-background text-foreground relative overflow-hidden py-28 md:py-40">
      {}
      <svg aria-hidden="true" viewBox="0 0 720 720" className="pointer-events-none absolute -top-20 -right-20 h-[360px] w-[360px] opacity-[0.08] md:h-[720px] md:w-[720px]" fill="none">
        <g stroke="currentColor" strokeWidth="2" strokeLinecap="round" className="text-primary">
          <path d="M360 60 C 380 220, 420 360, 560 480" />
          <path d="M360 60 C 320 200, 260 320, 120 440" />
          <path d="M360 60 L 360 540" />
          {Array.from({
          length: 14
        }).map((_, i) => {
          const t = (i + 1) / 15;
          const y = 60 + t * 480;
          const spread = 40 + t * 220;
          return <g key={i} data-index={i}>
                <path d={`M360 ${y} Q ${360 - spread / 2} ${y + 20}, ${360 - spread} ${y + 60}`} />
                <path d={`M360 ${y} Q ${360 + spread / 2} ${y + 20}, ${360 + spread} ${y + 60}`} />
              </g>;
        })}
        </g>
      </svg>

      <div className="relative mx-auto max-w-[1360px] px-6 md:px-10 lg:px-16">
        {}
        <div ref={headerRef} className={`grid grid-cols-1 gap-10 lg:grid-cols-12 lg:gap-8 transition-all duration-700 ease-out ${headerVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}>
          <div className="lg:col-span-7">
            <div className="flex items-center gap-2 font-mono text-xs uppercase tracking-[0.18em] text-muted-foreground">
              <span>DIVISIÓN ACERO</span>
              <Asterisk className="size-3.5 text-accent" strokeWidth={2.5} />
              <span>EL TALENTO DETRÁS DE LA FORJA</span>
            </div>

            <h2 className="mt-6 font-default text-[clamp(2.25rem,4.8vw,4.25rem)] font-bold leading-[1.02] tracking-tight text-foreground">
              Liderazgo sólido,{" "}
              <span className="relative inline-block italic text-primary">
                compromiso
                {}
                <svg aria-hidden="true" viewBox="0 0 240 16" preserveAspectRatio="none" className="absolute -bottom-2 left-0 h-3 w-full" fill="none">
                  <path d="M2 9 Q 30 2, 60 8 T 120 9 T 180 7 T 238 10" stroke="var(--accent)" strokeWidth="2.5" strokeLinecap="round" />
                </svg>
              </span>{" "}
              de acero.
            </h2>
          </div>

          <div className="lg:col-span-5 lg:pt-4">
            <p className="font-default text-[clamp(1.0625rem,1.15vw,1.25rem)] leading-[1.55] text-muted-foreground">
              Conozca al equipo de expertos de Kalisch Acero: ingenieros, técnicos y especialistas 
              dedicados a suministrar soluciones estructurales de alta resistencia para los 
              proyectos más ambiciosos de México.
            </p>
          </div>
        </div>

        {}
        <div className="mt-16 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4 lg:gap-7">
          {team.map((member, i) => <Card key={member.name} data-index={i} className="group flex flex-col gap-5 rounded-md border-[1.5px] border-border bg-card p-5 text-card-foreground shadow-md transition-all duration-[280ms] ease-out hover:-translate-y-1 hover:border-primary">
              <div className="relative aspect-square overflow-hidden rounded-md">
                <img src={member.image} data-wvc-srcset={member.srcset} data-wvc-sizes={member.sizes} alt={`Retrato de ${member.name}`} loading="lazy" decoding="async" className="h-full w-full object-cover transition-transform duration-[420ms] ease-out group-hover:scale-[1.03]" />
                {}
                <svg aria-hidden="true" viewBox="0 0 200 200" className="pointer-events-none absolute inset-0 h-full w-full opacity-[0.12]" fill="none">
                  <g stroke="var(--primary)" strokeWidth="1.5" strokeLinecap="round">
                    <path d="M20 10 C 60 70, 100 130, 180 190" />
                    <path d="M20 10 Q 40 60, 70 90" />
                    <path d="M50 40 Q 70 70, 90 90" />
                    <path d="M80 70 Q 100 100, 120 120" />
                    <path d="M110 100 Q 130 130, 150 150" />
                  </g>
                </svg>
              </div>

              <div className="flex flex-col gap-3 px-1 pb-1">
                <Badge className="self-start rounded-sm bg-primary/10 px-2.5 py-1 font-mono text-[0.6875rem] font-semibold uppercase tracking-[0.12em] text-primary">
                  {member.role}
                </Badge>
                <h3 className="font-default text-[clamp(1.5rem,2vw,1.875rem)] font-bold leading-[1.15] tracking-tight text-foreground">
                  {member.name}
                </h3>
                <p className="font-default text-sm leading-[1.55] text-muted-foreground">
                  {member.bio}
                </p>
              </div>
            </Card>)}

          {}
          <Card data-index={3} className="group relative flex flex-col justify-between overflow-hidden rounded-md border-[1.5px] border-border bg-card p-7 text-card-foreground shadow-md transition-all duration-[280ms] ease-out hover:-translate-y-1 hover:border-primary">
            {}
            <div aria-hidden="true" className="pointer-events-none absolute -bottom-32 left-1/2 h-64 w-64 -translate-x-1/2 rounded-md bg-primary/5" />

            <div className="relative z-10 flex items-center gap-2 font-mono text-[0.6875rem] uppercase tracking-[0.18em] text-muted-foreground">
              <Asterisk className="size-3 text-accent" strokeWidth={2.5} />
              <span>VACANTES</span>
            </div>

            <div className="relative z-10 mt-6 flex flex-col gap-5">
              <h3 className="font-default text-[clamp(1.5rem,2vw,1.875rem)] font-bold leading-[1.1] tracking-tight text-foreground">
                ¿Quieres unirte a Kalisch?
              </h3>
              <p className="font-default text-sm leading-[1.55] text-muted-foreground">
                Buscamos profesionales con temple y visión. Únete a la empresa líder en acero.
              </p>

              <Link to="/AboutUsPage" className="self-start">
                <Button variant="link" className="h-auto gap-1.5 px-0 font-default text-base font-semibold text-primary no-underline hover:no-underline">
                  <span className="relative">
                    Postularse
                    <svg aria-hidden="true" viewBox="0 0 80 10" preserveAspectRatio="none" className="absolute -bottom-1.5 left-0 h-2 w-full transition-all duration-[220ms] ease-out group-hover:w-[110%]" fill="none">
                      <path d="M2 6 Q 20 1, 40 5 T 78 5" stroke="var(--accent)" strokeWidth="2.5" strokeLinecap="round" />
                    </svg>
                  </span>
                  <ArrowRight className="size-4 transition-transform duration-[220ms] group-hover:translate-x-1" />
                </Button>
              </Link>
            </div>
          </Card>
        </div>
      </div>
    </section>;
}